// src/modals/beerModals.jsx
// Modals related to beer management (filling, rating, feedback, history)
import React, { useMemo } from 'react';
// import { Star } from 'lucide-react';
import { BJCP_STYLES, renderStars, getSelectableLocations } from '../kegops-utils';

// Fill Keg Modal - Fill empty keg with new beer
export const FillKegModal = ({ 
  show, onClose, newBeer, setNewBeer, styleSearch, setStyleSearch, 
  showStyleDropdown, setShowStyleDropdown, beerImagePreview, onImageUpload, onFill, isEditing, locations, selectedKeg
}) => {
  const filteredStyles = useMemo(() => {
    if (!styleSearch) return BJCP_STYLES;
    const searchLower = styleSearch.toLowerCase();
    return BJCP_STYLES.filter(style => {
      const styleLower = style.toLowerCase();
      if (styleLower.includes(searchLower)) return true;
      let searchIndex = 0;
      for (let i = 0; i < styleLower.length && searchIndex < searchLower.length; i++) {
        if (styleLower[i] === searchLower[searchIndex]) searchIndex++;
      }
      return searchIndex === searchLower.length;
    }).slice(0, 10);
  }, [styleSearch]);

  // Get flattened location/sublocation options
  const selectableLocations = useMemo(() => getSelectableLocations(locations), [locations]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full my-8">
        <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">{isEditing ? 'Edit Current Beer' : 'Fill Keg with New Beer'}</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block mb-1 font-medium text-gray-900 dark:text-gray-100">Location*</label>
            <select
              value={newBeer.location}
              onChange={(e) => setNewBeer({...newBeer, location: e.target.value})}
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            >
              <option value="">Select Location...</option>
              {selectableLocations.map(loc => (
                <option key={loc.id} value={loc.name}>{loc.displayName}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block mb-1 font-medium text-gray-900 dark:text-gray-100">Brewery</label>
            <input
              type="text"
              value={newBeer.brewery}
              onChange={(e) => setNewBeer({...newBeer, brewery: e.target.value})}
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              placeholder="Brewery Name"
            />
          </div>
          <div>
            <label className="block mb-1 font-medium text-gray-900 dark:text-gray-100">Beer Name*</label>
            <input
              type="text"
              value={newBeer.name}
              onChange={(e) => setNewBeer({...newBeer, name: e.target.value})}
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              placeholder="West Coast IPA"
            />
          </div>

          <div className="relative">
            <label className="block mb-1 font-medium text-gray-900 dark:text-gray-100">Style*</label>
            <input
              type="text"
              value={styleSearch || newBeer.style}
              onChange={(e) => {
                setStyleSearch(e.target.value);
                setNewBeer({...newBeer, style: e.target.value});
                setShowStyleDropdown(true);
              }}
              onFocus={() => setShowStyleDropdown(true)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              placeholder="Start typing to search BJCP styles..."
            />
            {showStyleDropdown && filteredStyles.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {filteredStyles.map((style, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      setNewBeer({...newBeer, style: style});
                      setStyleSearch("");
                      setShowStyleDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-amber-50 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-600 last:border-b-0"
                  >
                    {style}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block mb-1 font-medium text-gray-900 dark:text-gray-100">Description (optional)</label>
            <textarea
              value={newBeer.description}
              onChange={(e) => setNewBeer({...newBeer, description: e.target.value})}
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              rows="2"
              placeholder="Brief description of the beer..."
            />
          </div>

          <div>
            <label className="block mb-1 font-medium text-gray-900 dark:text-gray-100">Beer Image (optional)</label>
            <input
              type="file"
              accept="image/*"
              onChange={onImageUpload}
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            {beerImagePreview && (
              <div className="mt-2 flex justify-center">
                <img 
                  src={beerImagePreview} 
                  alt="Preview" 
                  className="max-w-full max-h-48 w-auto h-auto object-contain rounded border border-gray-300 dark:border-gray-600"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block mb-1 font-medium text-gray-900 dark:text-gray-100">ABV*</label>
              <input
                type="number"
                step="0.1"
                value={newBeer.abv}
                onChange={(e) => setNewBeer({...newBeer, abv: e.target.value})}
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="6.8"
              />
            </div>
            <div>
              <label className="block mb-1 font-medium text-gray-900 dark:text-gray-100">IBU</label>
              <input
                type="number"
                value={newBeer.ibu}
                onChange={(e) => setNewBeer({...newBeer, ibu: e.target.value})}
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="65"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block mb-1 font-medium text-gray-900 dark:text-gray-100">OG</label>
              <input
                type="number"
                step="0.001"
                value={newBeer.og}
                onChange={(e) => setNewBeer({...newBeer, og: e.target.value})}
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="1.062"
              />
            </div>
            <div>
              <label className="block mb-1 font-medium text-gray-900 dark:text-gray-100">FG</label>
              <input
                type="number"
                step="0.001"
                value={newBeer.fg}
                onChange={(e) => setNewBeer({...newBeer, fg: e.target.value})}
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="1.012"
              />
            </div>
          </div>

          <div>
            <label className="block mb-1 font-medium text-gray-900 dark:text-gray-100">Batch Size (gallons)</label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={newBeer.batchVolume || ''}
              onChange={(e) => setNewBeer({...newBeer, batchVolume: e.target.value})}
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              placeholder="e.g., 4.0"
            />
            {selectedKeg && selectedKeg.kegDetails && (
              <p className="text-xs text-gray-500 mt-1">Keg capacity: {selectedKeg.kegDetails.volume} gallons</p>
            )}
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button
            onClick={onFill}
            disabled={!newBeer.name || !newBeer.style || !newBeer.abv || !newBeer.location}
            className="flex-1 bg-green-600 text-white py-2 rounded hover:bg-green-700 transition disabled:bg-gray-300 dark:disabled:bg-gray-600"
          >
            {isEditing ? 'Save Changes' : 'Fill Keg'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-900 dark:text-gray-100 py-2 rounded hover:bg-gray-400 dark:hover:bg-gray-500 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Rating Modal - Rate beer without kicking it
export const RatingModal = ({ show, onClose, beer, ratingForm, setRatingForm, onSubmit }) => {
  if (!show || !beer) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h2 className="text-xl font-bold mb-4">Rate This Beer</h2>
        <p className="mb-4">Beer: <span className="font-medium">{beer.name}</span></p>
        
        <div className="mb-4">
          <label className="block mb-2 font-medium">Rating*</label>
          {renderStars(ratingForm.rating, true, (rating) => setRatingForm({...ratingForm, rating}))}
        </div>

        <div className="mb-4">
          <label className="block mb-2 font-medium">Your Name (optional)</label>
          <input
            type="text"
            value={ratingForm.raterName}
            onChange={(e) => setRatingForm({...ratingForm, raterName: e.target.value})}
            className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder="Enter your name"
          />
        </div>

        <div className="mb-4">
          <label className="block mb-2 font-medium">Notes</label>
          <textarea
            value={ratingForm.notes}
            onChange={(e) => setRatingForm({...ratingForm, notes: e.target.value})}
            className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            rows="3"
            placeholder="Optional feedback..."
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={onSubmit}
            disabled={ratingForm.rating === 0}
            className="flex-1 bg-amber-600 text-white py-2 rounded hover:bg-amber-700 transition disabled:bg-gray-300"
          >
            Submit Rating
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-300 py-2 rounded hover:bg-gray-400 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Feedback Modal - Rate beer when marking keg as kicked
export const FeedbackModal = ({ 
  show, onClose, beer, feedbackRating, setFeedbackRating, 
  feedbackNotes, setFeedbackNotes, feedbackName, setFeedbackName, onSubmit 
}) => {
  if (!show || !beer) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h2 className="text-xl font-bold mb-4">Beer Feedback</h2>
        <p className="mb-4">Beer: <span className="font-medium">{beer.name}</span></p>
        
        <div className="mb-4">
          <label className="block mb-2 font-medium">Rating*</label>
          {renderStars(feedbackRating, true, setFeedbackRating)}
        </div>

        <div className="mb-4">
          <label className="block mb-2 font-medium">Your Name (optional)</label>
          <input
            type="text"
            value={feedbackName}
            onChange={(e) => setFeedbackName(e.target.value)}
            className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder="Enter your name"
          />
        </div>

        <div className="mb-4">
          <label className="block mb-2 font-medium">Notes</label>
          <textarea
            value={feedbackNotes}
            onChange={(e) => setFeedbackNotes(e.target.value)}
            className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            rows="3"
            placeholder="Optional feedback..."
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={onSubmit}
            disabled={feedbackRating === 0}
            className="flex-1 bg-amber-600 text-white py-2 rounded hover:bg-amber-700 transition disabled:bg-gray-300"
          >
            Submit
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-300 py-2 rounded hover:bg-gray-400 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Clean Modal - Choose sanitized or purged endpoint
export const CleanModal = ({ show, onClose, onClean }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h2 className="text-xl font-bold mb-4">Clean and Sanitize Keg</h2>
        <p className="mb-6 text-gray-600">Choose your cleaning endpoint:</p>
        
        <div className="flex gap-3">
          <button
            onClick={() => onClean("Sanitized")}
            className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-medium"
          >
            Sanitized
          </button>
          <button
            onClick={() => onClean("Purged")}
            className="flex-1 bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition font-medium"
          >
            Purged
          </button>
        </div>

        <button
          onClick={onClose}
          className="w-full mt-3 bg-gray-300 text-gray-700 py-2 rounded hover:bg-gray-400 transition"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// Beer History Modal - View all ratings and details for a beer
export const BeerHistoryModal = ({ show, onClose, beer, calculateAverageRating, getTotalRatings, renderStars }) => {
  if (!show || !beer) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full my-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">{beer.name}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-3xl font-bold leading-none"
          >
            ×
          </button>
        </div>
        
        {beer.imageUrl && (
          <div className="mb-4 flex justify-center">
            <img 
              src={beer.imageUrl} 
              alt={beer.name}
              className="max-w-full max-h-80 w-auto h-auto object-contain rounded-lg"
            />
          </div>
        )}
        
        <div className="bg-amber-50 p-4 rounded-lg mb-4">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><span className="font-medium">Style:</span> {beer.style}</div>
            <div><span className="font-medium">ABV:</span> {beer.abv}%</div>
            {beer.ibu && <div><span className="font-medium">IBU:</span> {beer.ibu}</div>}
            {beer.og && <div><span className="font-medium">OG:</span> {beer.og}</div>}
            {beer.fg && <div><span className="font-medium">FG:</span> {beer.fg}</div>}
          </div>
          {beer.description && (
            <div className="mt-2 pt-2 border-t border-amber-200">
              <span className="font-medium">Description:</span>
              <p className="text-sm mt-1">{beer.description}</p>
            </div>
          )}
        </div>

        <div className="mb-4 pb-4 border-b">
          <p className="text-sm text-gray-600 mb-2 flex items-center gap-2">
            <span>Overall Average:</span>
            {renderStars(calculateAverageRating(beer.sessions))}
            <span className="ml-2">({getTotalRatings(beer.sessions)} total rating{getTotalRatings(beer.sessions) !== 1 ? 's' : ''})</span>
          </p>
        </div>
        
        <div className="space-y-3">
          <h3 className="font-semibold text-lg">All Ratings:</h3>
          {beer.sessions.flatMap((session, sessionIdx) => 
            session.ratings.map((rating, ratingIdx) => (
              <div key={`${sessionIdx}-${ratingIdx}`} className="bg-gray-50 p-4 rounded border-l-4 border-amber-500">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {renderStars(rating.rating)}
                    {rating.isKickedRating && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium">Kicked Rating</span>
                    )}
                  </div>
                  <span className="text-sm text-gray-500">{rating.date} at {rating.time}</span>
                </div>
                {rating.raterName && (
                  <p className="text-sm font-medium text-amber-700 mb-1">By: {rating.raterName}</p>
                )}
                {rating.notes && (
                  <p className="text-sm text-gray-700 italic">"{rating.notes}"</p>
                )}
                <div className="text-xs text-gray-500 mt-2">
                  Location: {session.location || 'Unknown'} • Status: {session.tapStatus}
                </div>
              </div>
            ))
          )}
        </div>

        <button
          onClick={onClose}
          className="w-full mt-6 bg-gray-600 text-white py-2 rounded hover:bg-gray-700 transition"
        >
          Close
        </button>
      </div>
    </div>
  );
};